import React, { Component } from 'react';
import styled from 'styled-components';
import { system } from './system';
import Label from './Label';
import Field from './Field';

// Replicated from molekule/lib/Form/Input.js
// onChange API: (name, value, event) — call sites in this app use (name, value) or (_, value)
// onFocus API: (name) — call sites do () => something
// onBlur API: (name) — call sites do () => something

interface InputSys {
  [key: string]: any;
}

interface StyledInputInternalProps {
  $sys?: InputSys;
  $isFloatable?: boolean;
  $isFloating?: boolean;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
  $leftIconSize?: number;
  $rightIconSize?: number;
}

const StyledInputEl = styled.input<StyledInputInternalProps>`
  border: 1px solid
    ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyLight : '#E8E9EA'};
  height: 48px;
  display: block;
  outline: none;
  width: 100%;
  padding: 8px;
  padding-right: 24px;
  border-radius: ${(p: any) =>
    p.theme && p.theme.radius != null ? `${p.theme.radius}px` : '8px'};
  transition: 250ms all;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: ${(p: any) =>
    p.theme && p.theme.typography
      ? `${p.theme.typography.fontSize}px`
      : '16px'};
  color: ${(p: any) =>
    p.theme && p.theme.typography ? p.theme.typography.color : '#1B202B'};
  box-sizing: border-box;

  ${(p: StyledInputInternalProps) =>
    p.$hasLeftIcon && !p.$isFloatable
      ? `padding-left: ${(p.$leftIconSize || 16) + 12}px;`
      : ''}

  ${(p: StyledInputInternalProps) =>
    p.$hasRightIcon ? `padding-right: ${(p.$rightIconSize || 16) + 32}px;` : ''}

  ${(p: StyledInputInternalProps) =>
    p.$isFloating
      ? `
        line-height: 16px;
        padding-top: 16px;
        padding-bottom: 0px;
      `
      : ''}

  &:hover,
  &:active {
    border-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyDark : '#A4A6AA'};
  }

  &:focus {
    border-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.primary : '#0958F3'};
  }

  ::placeholder {
    color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyDarker : '#767980'};
    opacity: ${(p: StyledInputInternalProps) => (p.$isFloatable ? 0 : 1)};
  }

  &[disabled] {
    background-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.white : '#FFFFFF'};
    border-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyLight : '#E8E9EA'};
    color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.grey : '#D1D2D5'};

    ::placeholder {
      color: ${(p: any) =>
        p.theme && p.theme.colors ? p.theme.colors.grey : '#D1D2D5'};
    }
  }

  ${(p: any) => system(p.$sys, p.theme)}
`;

const StyledTextarea = styled.textarea<StyledInputInternalProps>`
  border: 1px solid
    ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyLight : '#E8E9EA'};
  display: block;
  outline: none;
  width: 100%;
  padding: 8px;
  border-radius: ${(p: any) =>
    p.theme && p.theme.radius != null ? `${p.theme.radius}px` : '8px'};
  transition: 250ms all;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: ${(p: any) =>
    p.theme && p.theme.typography
      ? `${p.theme.typography.fontSize}px`
      : '16px'};
  color: ${(p: any) =>
    p.theme && p.theme.typography ? p.theme.typography.color : '#1B202B'};
  box-sizing: border-box;
  resize: vertical;

  ${(p: StyledInputInternalProps) =>
    p.$isFloatable && p.$isFloating ? `padding-top: 24px;` : ''}

  &:hover,
  &:active {
    border-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyDark : '#A4A6AA'};
  }

  &:focus {
    border-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.primary : '#0958F3'};
  }

  ::placeholder {
    color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyDarker : '#767980'};
    opacity: ${(p: StyledInputInternalProps) => (p.$isFloatable ? 0 : 1)};
  }

  &[disabled] {
    background-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.white : '#FFFFFF'};
    border-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyLight : '#E8E9EA'};
    color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.grey : '#D1D2D5'};
  }

  ${(p: any) => system(p.$sys, p.theme)}
`;

const InputContainer = styled.div`
  position: relative;
`;

export const FormError = styled.span`
  display: block;
  color: ${(p: any) =>
    p.theme && p.theme.colors
      ? p.theme.colors.red || p.theme.colors.danger
      : '#FD575D'};
  font-size: 12px;
  margin-top: 4px;
`;

export interface InputProps {
  name?: string;
  id?: string;
  type?: string;
  value?: string | number;
  placeholder?: string;
  label?: React.ReactNode;
  error?: string;
  disabled?: boolean;
  multiline?: boolean;
  floating?: boolean;
  rows?: number;
  leftIcon?: string;
  leftIconProps?: Record<string, any>;
  rightIcon?: string;
  rightIconProps?: Record<string, any>;
  onChange?: (
    name: string,
    value: string,
    event?: React.ChangeEvent<any>,
  ) => void;
  onFocus?: (name: string) => void;
  onBlur?: (name: string) => void;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}

interface InputState {
  focused: boolean;
  value: string | number;
}

class Input extends Component<InputProps, InputState> {
  static defaultProps = {
    type: 'text',
    multiline: false,
    rows: 3,
    disabled: false,
    floating: false,
    leftIconProps: {},
    rightIconProps: {},
    onChange: () => {},
    onFocus: () => {},
    onBlur: () => {},
  };

  constructor(props: InputProps) {
    super(props);
    this.state = {
      focused: false,
      value: props.value != null ? props.value : '',
    };
  }

  static getDerivedStateFromProps(props: InputProps, state: InputState) {
    if (props.value !== undefined && props.value !== state.value) {
      return { value: props.value };
    }
    return null;
  }

  handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    this.setState({ focused: true });
    if (this.props.onFocus) {
      this.props.onFocus(e.target.name);
    }
  };

  handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    this.setState({ focused: false });
    if (this.props.onBlur) {
      this.props.onBlur(e.target.name);
    }
  };

  handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value;
    this.setState({ value: newValue });
    if (this.props.onChange) {
      this.props.onChange(e.target.name, newValue, e);
    }
  };

  render() {
    const {
      name,
      id,
      type,
      placeholder,
      label,
      error,
      disabled,
      multiline,
      floating,
      rows,
      leftIcon,
      leftIconProps,
      rightIcon,
      rightIconProps,
      className,
      style,
      onChange: _onChange,
      onFocus: _onFocus,
      onBlur: _onBlur,
      value: _value,
      ...rest
    } = this.props;

    // Strip non-DOM props
    const { styles, ...domRest } = rest as any;

    const { focused, value } = this.state;
    const $isFloating =
      (floating && value != null && `${value}`.trim() !== '') ||
      (floating && focused);

    const statusProps = {
      $isFloatable: floating,
      $isFloating: $isFloating,
    };

    const inputEl = multiline ? (
      <StyledTextarea
        {...statusProps}
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        style={style}
        {...domRest}
      />
    ) : (
      <StyledInputEl
        {...statusProps}
        $hasLeftIcon={!!leftIcon}
        $hasRightIcon={!!rightIcon}
        $leftIconSize={leftIconProps && leftIconProps.size}
        $rightIconSize={rightIconProps && rightIconProps.size}
        id={id}
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        style={style}
        {...domRest}
      />
    );

    const labelEl = label ? (
      <Label
        htmlFor={id}
        isFloatable={floating}
        isFloating={$isFloating}
        isFocused={focused}
        isDisabled={disabled}
        multiline={multiline}
      >
        {label}
      </Label>
    ) : null;

    return (
      <Field className={className}>
        {!floating && labelEl}
        <InputContainer>
          {floating && labelEl}
          {inputEl}
        </InputContainer>
        {!focused && error ? <FormError>{error}</FormError> : null}
      </Field>
    );
  }
}

export default Input;
